import { t } from '@lingui/macro'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'

import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { BookingFlowActions, BookingFlowCenteredContent, BookingFlowLayout } from './BookingFlowLayout'
import { RecommendedCaregiverSelect } from './RecommendedCaregiverSelect'

import type { FlowState } from '../types/booking.type'

type RecommendedCaregiversContentProps = {
  flowState: FlowState
  onAction: (
    action: 'select-recommended-caregiver',
    payload: {
      recommendedCaregiverId: number | null
    },
  ) => void
  isLoading?: boolean
}

export const RecommendedCaregiversContent = ({ flowState, onAction, isLoading }: RecommendedCaregiversContentProps) => {
  const [selectedCaregiver, setSelectedCaregiver] = useState<number | null>()

  const handleSelectCaregiver = (caregiverId: number | null) => {
    setSelectedCaregiver(caregiverId)
  }

  const handleContinue = () => {
    // Allow null for 'other doctor' option
    if (!selectedCaregiver) {
      return
    }

    // If the user chose 'other doctor', we need to send a null caregiverId
    onAction('select-recommended-caregiver', {
      recommendedCaregiverId: selectedCaregiver === -1 ? null : selectedCaregiver,
    })
  }

  return (
    <BookingFlowLayout>
      <BookingFlowCenteredContent>
        <AnimatePresence mode='wait' initial={false}>
          {isLoading ? (
            <motion.div key='loader' {...getDropInProps({ exit: { opacity: 0, y: -30 } })}>
              <AnimatedLoader small />
            </motion.div>
          ) : (
            <motion.div key='list' {...getDropInProps()}>
              <RecommendedCaregiverSelect
                caregivers={flowState.recommendedCaregivers || []}
                selectedCaregiverId={selectedCaregiver}
                onSelect={handleSelectCaregiver}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </BookingFlowCenteredContent>

      <BookingFlowActions>
        <Button
          variant='Primary'
          onClick={handleContinue}
          disabled={!selectedCaregiver || isLoading}
          isLoading={isLoading}
          data-testid='recommended-caregivers-continue-button'
        >
          {t({
            id: 'global.continue',
            message: 'Ga verder',
          })}
        </Button>
      </BookingFlowActions>
    </BookingFlowLayout>
  )
}
